/**
 * @name accommodationRequests
 * @description Function used to make requests to the accommodation endpoints
 * @param requestor
 * @returns {object} accommodationRequests
 */
export const accommodationRequests = (requestor: any) => ({
  /**
   * @name createAccommodation
   * @description Function used to make request to create an accommodation
   * @param {object} data
   * @returns {Promise<any>} response
   */
  createAccommodation: async (data: any) => {
    const url = `/accommodations/create`;
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.descriptionTitle) formData.append('descriptionTitle', data.descriptionTitle);
    formData.append('description', data.description);
    formData.append('type', data.type);
    formData.append('location', data.location);
    formData.append('country', data.country);
    if (data.tours && data.tours.length > 0) formData.append('tours', data.tours);
    if (data.file) formData.append('file', data.file);
    if (data.blocks) formData.append('blocks', JSON.stringify(data.blocks));
    const response = await requestor({
      method: 'POST',
      url,
      data: formData,
    });
    return response;
  },

  /**
   * @name editAccommodation
   * @description Function used to make request to edit an accommodation
   * @param {object} data
   * @param {string} id
   * @returns {Promise<any>} response
   */
  editAccommodation: async (data: any, id: string) => {
    const url = `/accommodations/update/${id}`;
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.descriptionTitle) formData.append('descriptionTitle', data.descriptionTitle);
    formData.append('description', data.description);
    formData.append('type', data.type);
    formData.append('location', data.location);
    formData.append('country', data.country);
    if (data.tours && data.tours.length > 0) formData.append('tours', data.tours);
    if (data.file) formData.append('file', data.file);
    if (data.blocks) formData.append('blocks', JSON.stringify(data.blocks));
    const response = await requestor({
      method: 'PATCH',
      url,
      data: formData,
    });
    return response;
  },

  /**
   * @name deleteAccommodation
   * @description Function used to make request to delete an accommodation
   * @param {string} id
   * @returns {object} response
   */
  deleteAccommodation: async (id: string) => {
    const url = `/accommodations/delete/${id}`;
    const response = await requestor({
      method: 'DELETE',
      url,
    });
    return response;
  },

  /**
   * @name get - handle all get requests for accommodations
   */
  get: {
    /**
     * @name getAllAccommodations
     * @description Function used to get all accommodations
     * @returns {object} response
     */
    getAllAccommodations: async () => {
      const url = `/accommodations/getAllAccommodations`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },

    /**
     * @name getAccommodationsByCountry
     * @description Function used to get accommodations by country
     * @param {string} countryId
     * @returns {object} response
     */
    getAccommodationsByCountry: async (countryId: string) => {
      const url = `/accommodations/getByCountry/${countryId}`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },

    /**
     * @name getAccommodationById
     * @description Function used to get an accommodation by id
     * @param {string} id
     * @returns {object} response
     */
    getAccommodationById: async (id: string) => {
      const url = `/accommodations/${id}`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },

    /**
     * @name getAccommodationBySlug
     * @description Function used to get an accommodation by slug
     * @param {string} slug
     * @returns {object} response
     */
    getAccommodationBySlug: async (slug: string) => {
      const url = `/accommodations/getBySlug/${slug}`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },
  },
});
